import React, { useEffect } from "react";
import styles from "../../pages/convert/components/styles/settings-drop-down.module.scss";
import activeAdvancedSettingsIcon from "@/assets/convert/advanced-active.svg";
import arrowDown from "@/assets/sidebar/arrow-down.svg";
import arrowUp from "@/assets/convert/arrow-up.svg";
import Image from "next/image";
import removeReverbIcon from "@/assets/convert/remove-reverb.svg";
import ConvertSettingButton from "@/components/pages/convert/components/convert-setting-button";
import TextButton from "@/components/common/text-button";
import ConversionEffectCard from "@/components/pages/convert/components/conversion-effect-card";
import ConversionEffectParameters from "@/components/pages/convert/components/conversion-effect-parameters";
import {
  ConvertBaseContextState,
  ConvertBaseSettings,
} from "@/hooks/ai-tool-process/use-convert-base-context-provider";

interface SettingsDropDownProps<TSettings extends ConvertBaseSettings> {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  context: ConvertBaseContextState<TSettings>;
}

const ConvertBaseSettingsDropDown = <TSettings extends ConvertBaseSettings>({
  isOpen,
  setIsOpen,
  context,
}: SettingsDropDownProps<TSettings>) => {
  const {
    settings,
    effectTabs,
    selectedEffectTab,
    handleTabSelection,
    isTabEnabled,
    handleEffectTabSwitcherClick,
    isTabSelected,
    handleResetAllEffectTabsClick,
    setRemoveReverbEcho,
    handleResetEffectTabParametersClick,
    handleEffectParameterValueChanged,
  } = context;

  useEffect(() => {
    effectTabs
      .filter((tab) => tab.isEnabledByDefault && !isTabEnabled(tab))
      .forEach((tab) => handleEffectTabSwitcherClick(tab));
  }, []);

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={styles.dropdown}>
      <div className={styles.header} role="button" onClick={handleClick}>
        <div className={styles.button_container}>
          <Image
            className={styles.icon}
            src={activeAdvancedSettingsIcon}
            alt="Settings & Effects"
          />
          <div className={styles.title}>Settings & Effects</div>
        </div>
        <Image
          className={styles.arrow_icon}
          src={isOpen ? arrowUp : arrowDown}
          alt="arrow"
        />
      </div>
      {isOpen && (
        <div className={styles.settings_menu}>
          <div className={styles.main_text}>Isolate Vocals</div>
          <ConvertSettingButton
            title="Remove Reverb / Echo"
            image={removeReverbIcon}
            value={settings.vocalIsolationSettings.removeReverbAndEcho}
            onChange={(isChecked: boolean) => setRemoveReverbEcho(isChecked)}
          />
          <div className={styles.conversion_effects_header}>
            <div className={styles.main_text}>Conversion Effects</div>
            <div className={styles.conversion_effect_header_description}>
              <div className={styles.secondary_text}>
                Adjust the output audio for instant release-ready sound
              </div>
              <TextButton
                isEnabled={true}
                text="Reset all Effects"
                textColor="#E64C4C"
                handleClick={handleResetAllEffectTabsClick}
                textPointeroverColor="#E64C4C"
              />
            </div>
          </div>
          <div className={styles.effects_container}>
            {effectTabs.map((tab) => (
              <ConversionEffectCard
                key={tab.id}
                isSelected={isTabSelected(tab)}
                isEnabled={isTabEnabled(tab)}
                handleClick={() => handleTabSelection(tab)}
                handleSwitcherClick={() => handleEffectTabSwitcherClick(tab)}
                iconPath={tab.iconPath}
                title={tab.displayName}
                tooltip={tab.tooltip}
              />
            ))}
          </div>
          {selectedEffectTab && (
            <ConversionEffectParameters
              tab={selectedEffectTab}
              settings={settings}
              handleResetEffectTabParametersClick={
                handleResetEffectTabParametersClick
              }
              handleEffectParameterValueChanged={
                handleEffectParameterValueChanged
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ConvertBaseSettingsDropDown;
